import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEnvelope, faLock, faBuilding } from "@fortawesome/free-solid-svg-icons";
import { loginUser, searchTenantNames } from "./AuthApi";
import { useAuth } from "../../context/AuthContext";

const Login: React.FC = () => {
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [tenantName, setTenantName] = useState("");
  const [tenantSuggestions, setTenantSuggestions] = useState<string[]>([]);
  const [showTenantDropdown, setShowTenantDropdown] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();
  const { login } = useAuth();

  // Fetch tenant suggestions
  useEffect(() => {
    const fetchTenants = async () => {
      if (tenantName.length >= 3) {
        try {
          const response = await searchTenantNames(tenantName);
          setTenantSuggestions(response.data || []);
          setShowTenantDropdown(true);
        } catch (err) {
          console.error("Tenant search failed", err);
        }
      } else {
        setTenantSuggestions([]);
        setShowTenantDropdown(false);
      }
    };

    fetchTenants();
  }, [tenantName]);

  const handleTenantSelect = (name: string) => {
    setTenantName(name);
    setShowTenantDropdown(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const response = await loginUser(identifier, password, tenantName);
      const { accessToken, refreshToken, user } = response.data;

      login({ accessToken, refreshToken, user });
      navigate("/");
    } catch (err: any) {
      setError(err.response?.data?.message || "Invalid credentials. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-100 to-indigo-200">
      <div className="bg-white p-8 rounded-lg shadow-lg max-w-md w-full">
        <h2 className="text-2xl font-bold mb-6 text-center text-indigo-700">Welcome Back</h2>

        {error && <div className="bg-red-100 text-red-700 p-3 rounded mb-4 text-sm">{error}</div>}

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Email or Mobile */}
          <div>
            <label className="block text-sm font-medium mb-1 text-gray-700">Email or Mobile</label>
            <div className="relative">
              <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-gray-400">
                <FontAwesomeIcon icon={faEnvelope} />
              </span>
              <input
                type="text"
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded focus:outline-none focus:border-indigo-500"
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                required
              />
            </div>
          </div>

          {/* Password */}
          <div>
            <label className="block text-sm font-medium mb-1 text-gray-700">Password</label>
            <div className="relative">
              <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-gray-400">
                <FontAwesomeIcon icon={faLock} />
              </span>
              <input
                type="password"
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded focus:outline-none focus:border-indigo-500"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>
          </div>

          {/* Tenant Name with Suggestions */}
          <div className="relative">
            <label className="block text-sm font-medium mb-1 text-gray-700">Tenant Name</label>
            <div className="relative">
              <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-gray-400">
                <FontAwesomeIcon icon={faBuilding} />
              </span>
              <input
                type="text"
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded focus:outline-none focus:border-indigo-500"
                value={tenantName}
                onChange={(e) => setTenantName(e.target.value)}
                required
              />
            </div>

            {showTenantDropdown && tenantSuggestions.length > 0 && (
              <ul className="absolute z-10 w-full bg-white border border-gray-300 rounded mt-1 shadow-md max-h-48 overflow-y-auto">
                {tenantSuggestions.map((tenant, index) => (
                  <li
                    key={index}
                    className="px-4 py-2 cursor-pointer hover:bg-indigo-100"
                    onClick={() => handleTenantSelect(tenant)}
                  >
                    {tenant}
                  </li>
                ))}
              </ul>
            )}
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-indigo-600 text-white py-2 rounded hover:bg-indigo-700 transition disabled:opacity-50"
          >
            {loading ? "Logging in..." : "Login"}
          </button>
        </form>
      </div>
    </div>
  );
};

export default Login;